interface ValidationResult {
  isValid: boolean;
  message?: string;
}

export class FieldValidator {
  static validateEmail(email: string): ValidationResult {
    if (!email) {
      return { isValid: true }; // Empty is valid (optional field)
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return {
        isValid: false,
        message: 'Please enter a valid email address (e.g., user@example.com)'
      };
    }

    return { isValid: true };
  }

  static validateURL(url: string): ValidationResult {
    if (!url) {
      return { isValid: true }; // Empty is valid (optional field)
    }

    try {
      // Add protocol if missing
      const urlWithProtocol = url.startsWith('http://') || url.startsWith('https://') 
        ? url 
        : `https://${url}`;
      
      const urlObj = new URL(urlWithProtocol);
      
      // Basic validation
      if (!urlObj.hostname.includes('.')) {
        return {
          isValid: false,
          message: 'Please enter a valid URL (e.g., example.com or https://example.com)'
        };
      }

      return { isValid: true };
    } catch {
      return {
        isValid: false,
        message: 'Please enter a valid URL (e.g., example.com or https://example.com)'
      };
    }
  }

  static validatePhone(phone: string): ValidationResult {
    if (!phone) {
      return { isValid: true }; // Empty is valid (optional field)
    }

    // Remove all non-digit characters for validation
    const cleanPhone = phone.replace(/\D/g, '');
    
    // Phone should be 10-15 digits (international format)
    if (cleanPhone.length < 10 || cleanPhone.length > 15) {
      return {
        isValid: false,
        message: 'Phone number should be 10-15 digits long'
      };
    }

    // Check if it starts with valid country codes or local format
    const phoneRegex = /^(\+91|91|0)?[6-9]\d{9}$/; // Indian format primarily
    const internationalRegex = /^(\+\d{1,3})?\d{10,14}$/; // General international

    if (!phoneRegex.test(phone) && !internationalRegex.test(phone)) {
      return {
        isValid: false,
        message: 'Please enter a valid phone number (e.g., +91 9876543210 or 9876543210)'
      };
    }

    return { isValid: true };
  }

  static validateNumber(number: string, fieldType: string): ValidationResult {
    if (!number) {
      return { isValid: true }; // Empty is valid (optional field)
    }

    const numValue = parseFloat(number);
    
    if (isNaN(numValue)) {
      return {
        isValid: false,
        message: 'Please enter a valid number'
      };
    }

    // Special validation for amount fields
    if (fieldType === 'amount') {
      if (numValue < 0) {
        return {
          isValid: false,
          message: 'Amount cannot be negative'
        };
      }
      
      // Check for reasonable decimal places (max 2 for currency)
      const decimalPlaces = (number.split('.')[1] || '').length;
      if (decimalPlaces > 2) {
        return {
          isValid: false,
          message: 'Amount can have maximum 2 decimal places'
        };
      }
    }

    return { isValid: true };
  }

  static validateField(value: string, fieldType: string): ValidationResult {
    switch (fieldType) {
      case 'email':
        return this.validateEmail(value);
      case 'url':
        return this.validateURL(value);
      case 'phone':
        return this.validatePhone(value);
      case 'number':
      case 'amount':
        return this.validateNumber(value, fieldType);
      default:
        return { isValid: true }; // No validation for other field types
    }
  }

  static formatValue(value: string, fieldType: string): string {
    switch (fieldType) {
      case 'email':
        return value.toLowerCase().trim();
      case 'url':
        // Add https:// if no protocol is specified
        if (value && !value.startsWith('http://') && !value.startsWith('https://')) {
          return `https://${value.trim()}`;
        }
        return value.trim();
      case 'phone':
        // Remove extra spaces and format consistently
        return value.replace(/\s+/g, ' ').trim();
      case 'amount':
        // Format to 2 decimal places if it's a valid number
        const num = parseFloat(value);
        return isNaN(num) ? value : num.toFixed(2);
      default:
        return value.trim();
    }
  }
}

export default FieldValidator;